import { useState, useEffect, useCallback, useRef } from 'react';
import { ActivityItem, ActivityType } from '../components/os/ActivityStream';
import { TradeProposal } from '../components/os/ProposalCard';

// WebSocket Message Types
export type MessageType =
  | 'AGENT_STARTED'
  | 'AGENT_STEP'
  | 'AGENT_COMPLETED'
  | 'AGENT_ERROR'
  | 'TRADE_CREATED'
  | 'TRADE_SUBMITTED'
  | 'COMPLIANCE_CHECK'
  | 'TRADE_APPROVED'
  | 'TRADE_REJECTED'
  | 'TRADE_SENT'
  | 'TRADE_EXECUTED'
  | 'TRADE_POSTED'
  | 'OPPORTUNITY_DISCOVERED'
  | 'LIQUIDITY_ASSESSMENT'
  | 'CONSENSUS_REACHED'
  | 'MEDIATION_REQUIRED'
  | 'PROPOSAL_CREATED'
  | 'PROPOSAL_UPDATED'
  | 'SYSTEM_ALERT'
  | 'CONNECTION_STATUS';

export interface WebSocketMessage {
  type: MessageType;
  agentType?: string;
  sessionId?: string;
  payload: any;
  timestamp: string;
}

export interface UseOMSWebSocketOptions {
  url?: string;
  topics?: string[];
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
  reconnectInterval?: number;
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
}

export interface UseOMSWebSocketReturn {
  isConnected: boolean;
  activities: ActivityItem[];
  proposals: TradeProposal[];
  lastMessage: WebSocketMessage | null;
  error: string | null;
  send: (message: any) => void;
  subscribe: (topics: string[]) => void;
  unsubscribe: (topics: string[]) => void;
  clearActivities: () => void;
  clearProposals: () => void;
  reconnect: () => void;
}

const DEFAULT_WS_URL = 'ws://localhost:8081/ws/agents';
const DEFAULT_TOPICS = ['*'];
const MAX_ACTIVITIES = 100;
const MAX_PROPOSALS = 50;

export function useOMSWebSocket(options: UseOMSWebSocketOptions = {}): UseOMSWebSocketReturn {
  const {
    url = DEFAULT_WS_URL,
    topics = DEFAULT_TOPICS,
    autoReconnect = true,
    maxReconnectAttempts = 5,
    reconnectInterval = 3000,
    onMessage,
    onConnect,
    onDisconnect,
    onError,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [proposals, setProposals] = useState<TradeProposal[]>([]);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Convert WebSocket message to Activity
  const messageToActivity = useCallback((message: WebSocketMessage): ActivityItem | null => {
    const baseActivity = {
      id: `${message.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(message.timestamp),
    };

    switch (message.type) {
      case 'AGENT_STARTED':
        return {
          ...baseActivity,
          type: 'system_event',
          title: `${message.agentType} Agent Started`,
          description: message.payload?.description || 'Processing...',
          status: 'in_progress',
          metadata: { agentType: message.agentType, sessionId: message.sessionId },
        };

      case 'AGENT_STEP':
        return {
          ...baseActivity,
          type: 'system_event',
          title: message.payload?.action || 'Agent Step',
          description: message.payload?.details ? JSON.stringify(message.payload.details) : '',
          status: 'in_progress',
          metadata: { agentType: message.agentType, sessionId: message.sessionId },
        };

      case 'AGENT_COMPLETED':
        return {
          ...baseActivity,
          type: 'system_event',
          title: `${message.agentType} Agent Completed`,
          description: message.payload?.summary || 'Task completed',
          status: 'success',
          metadata: { agentType: message.agentType, sessionId: message.sessionId, results: message.payload?.results },
        };

      case 'AGENT_ERROR':
        return {
          ...baseActivity,
          type: 'system_event',
          title: `${message.agentType} Agent Error`,
          description: message.payload?.error || 'An error occurred',
          status: 'error',
          metadata: { agentType: message.agentType, sessionId: message.sessionId },
        };

      case 'TRADE_CREATED':
        return {
          ...baseActivity,
          type: 'trade_created',
          title: 'Trade Created',
          description: `${message.payload?.side} ${message.payload?.quantity} ${message.payload?.security}`,
          status: 'success',
          side: message.payload?.side,
          quantity: message.payload?.quantity,
          security: message.payload?.security,
          metadata: message.payload,
        };

      case 'TRADE_SUBMITTED':
        return {
          ...baseActivity,
          type: 'trade_submitted',
          title: 'Trade Submitted for Pre-Clear',
          description: message.payload?.security || 'Trade submitted',
          status: 'pending',
          security: message.payload?.security,
          metadata: message.payload,
        };

      case 'COMPLIANCE_CHECK':
        return {
          ...baseActivity,
          type: 'compliance_check',
          title: 'Compliance Check',
          description: `Status: ${message.payload?.status || 'Unknown'}`,
          status: message.payload?.status === 'PASSED' ? 'success' :
                  message.payload?.status === 'FAILED' ? 'error' : 'warning',
          metadata: { ...message.payload, agentType: 'COMPLIANCE' },
        };

      case 'TRADE_APPROVED':
        return {
          ...baseActivity,
          type: 'trade_approved',
          title: 'Trade Approved',
          description: message.payload?.security || 'Trade approved',
          status: 'success',
          security: message.payload?.security,
          metadata: message.payload,
        };

      case 'TRADE_REJECTED':
        return {
          ...baseActivity,
          type: 'trade_rejected',
          title: 'Trade Rejected',
          description: message.payload?.reason || 'Trade rejected',
          status: 'error',
          metadata: message.payload,
        };

      case 'TRADE_SENT':
        return {
          ...baseActivity,
          type: 'trade_sent',
          title: 'Trade Sent to Venue',
          description: `Venue: ${message.payload?.venue || 'Unknown'}`,
          status: 'in_progress',
          metadata: message.payload,
        };

      case 'TRADE_EXECUTED':
        return {
          ...baseActivity,
          type: 'trade_executed',
          title: 'Trade Executed',
          description: `${message.payload?.side} ${message.payload?.quantity} @ ${message.payload?.price}`,
          status: 'success',
          side: message.payload?.side,
          quantity: message.payload?.quantity,
          price: message.payload?.price,
          metadata: message.payload,
        };

      case 'TRADE_POSTED':
        return {
          ...baseActivity,
          type: 'trade_posted',
          title: 'Trade Posted to IBOR',
          description: message.payload?.security || 'Trade posted',
          status: 'success',
          security: message.payload?.security,
          metadata: message.payload,
        };

      case 'OPPORTUNITY_DISCOVERED':
        return {
          ...baseActivity,
          type: 'system_event',
          title: 'Opportunity Discovered',
          description: message.payload?.description || 'New trading opportunity',
          status: 'success',
          metadata: { ...message.payload, agentType: 'RESEARCH' },
        };

      case 'LIQUIDITY_ASSESSMENT':
        return {
          ...baseActivity,
          type: 'system_event',
          title: 'Liquidity Assessment',
          description: `Status: ${message.payload?.status || 'Unknown'}`,
          status: message.payload?.status === 'GOOD' ? 'success' :
                  message.payload?.status === 'POOR' ? 'warning' : 'pending',
          metadata: { ...message.payload, agentType: 'EXECUTION' },
        };

      case 'CONSENSUS_REACHED':
        return {
          ...baseActivity,
          type: 'system_event',
          title: 'Consensus Reached',
          description: `Status: ${message.payload?.status || 'Unknown'}`,
          status: 'success',
          metadata: { ...message.payload, agentType: 'ORCHESTRATOR' },
        };

      case 'SYSTEM_ALERT':
        return {
          ...baseActivity,
          type: 'system_event',
          title: message.payload?.title || 'System Alert',
          description: message.payload?.message || '',
          status: message.payload?.severity === 'error' ? 'error' :
                  message.payload?.severity === 'warning' ? 'warning' : 'success',
        };

      default:
        return null;
    }
  }, []);

  // Convert WebSocket message to Proposal (for proposal-related messages)
  const messageToProposal = useCallback((message: WebSocketMessage): TradeProposal | null => {
    if (message.type === 'PROPOSAL_CREATED' || message.type === 'PROPOSAL_UPDATED') {
      const payload = message.payload;
      if (!payload) return null;

      return {
        id: payload.recommendationId || payload.id,
        type: payload.opportunity?.type?.toLowerCase() || 'new_trade',
        status: payload.status === 'APPROVED' ? 'approved' :
                payload.status === 'REJECTED' ? 'rejected' :
                payload.status === 'REQUIRES_HUMAN_REVIEW' ? 'pending' : 'pending',
        priority: payload.overallConfidence >= 0.8 ? 'high' :
                  payload.overallConfidence >= 0.6 ? 'medium' : 'low',
        createdAt: payload.timestamp,
        trade: {
          side: payload.opportunity?.type?.includes('BUY') ? 'BUY' : 'SELL',
          security: payload.opportunity?.description?.split(' ')[0] || 'Unknown',
          cusip: payload.opportunity?.cusip || '',
          quantity: 1000000,
          notional: 1000000,
          portfolioId: payload.opportunity?.affectedPortfolios?.[0] || '',
          portfolioName: 'Portfolio',
        },
        rationale: {
          summary: payload.finalReasoning || payload.opportunity?.rationale || '',
          factors: [],
          expectedOutcome: `Expected return: ${(payload.opportunity?.expectedReturn * 100 || 0).toFixed(2)}%`,
        },
        evidence: [],
        risk: {
          level: payload.overallConfidence >= 0.7 ? 'low' :
                 payload.overallConfidence >= 0.5 ? 'medium' : 'high',
          factors: [],
          complianceStatus: payload.complianceResult?.status === 'PASSED' ? 'passed' :
                            payload.complianceResult?.status === 'FAILED' ? 'failed' : 'pending',
        },
        agentAssessments: Object.entries(payload.agentVotes || {}).map(([agent, vote]) => ({
          agentType: agent as any,
          decision: vote === 'APPROVE' ? 'approve' : vote === 'REJECT' ? 'reject' : 'conditional',
          confidence: payload.overallConfidence || 0.5,
          reasoning: '',
        })),
        workflowStage: 'PENDING_PRECLEAR',
        sessionId: payload.sessionId,
      };
    }
    return null;
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const ws = new WebSocket(url);

      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;

        // Subscribe to topics
        if (topics.length > 0) {
          ws.send(JSON.stringify({ type: 'SUBSCRIBE', topics }));
        }

        onConnect?.();
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          setLastMessage(message);

          // Convert to activity
          const activity = messageToActivity(message);
          if (activity) {
            setActivities((prev) => [activity, ...prev].slice(0, MAX_ACTIVITIES));
          }

          // Convert to proposal if applicable
          const proposal = messageToProposal(message);
          if (proposal) {
            setProposals((prev) => {
              const existingIndex = prev.findIndex((p) => p.id === proposal.id);
              if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = proposal;
                return updated;
              }
              return [proposal, ...prev].slice(0, MAX_PROPOSALS);
            });
          }

          onMessage?.(message);
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;
        onDisconnect?.();

        // Auto-reconnect
        if (autoReconnect && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectInterval);
        }
      };

      ws.onerror = (event) => {
        setError('WebSocket connection error');
        onError?.(event);
      };

      wsRef.current = ws;
    } catch (e) {
      setError(`Failed to connect: ${e}`);
    }
  }, [url, topics, autoReconnect, maxReconnectAttempts, reconnectInterval,
      onConnect, onDisconnect, onError, onMessage, messageToActivity, messageToProposal]);

  // Disconnect
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  // Send message
  const send = useCallback((message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  }, []);

  // Subscribe to topics
  const subscribe = useCallback((newTopics: string[]) => {
    send({ type: 'SUBSCRIBE', topics: newTopics });
  }, [send]);

  // Unsubscribe from topics
  const unsubscribe = useCallback((topicsToRemove: string[]) => {
    send({ type: 'UNSUBSCRIBE', topics: topicsToRemove });
  }, [send]);

  // Clear activities
  const clearActivities = useCallback(() => {
    setActivities([]);
  }, []);

  // Clear proposals
  const clearProposals = useCallback(() => {
    setProposals([]);
  }, []);

  // Reconnect
  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect, disconnect]);

  // Connect on mount
  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    activities,
    proposals,
    lastMessage,
    error,
    send,
    subscribe,
    unsubscribe,
    clearActivities,
    clearProposals,
    reconnect,
  };
}

export default useOMSWebSocket;
